import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp } from "lucide-react";
import { BrandMark, Avatar } from "./BrandMark";
import { updatedLabel, relativeTime } from "@/lib/time";
import { TemplateInfoPopover } from "./TemplateInfoPopover";

const COLS = [
    { key: "name", label: "Name" },
    { key: "category", label: "Category" },
    { key: "brand", label: "Brand" },
    { key: "owner", label: "Owner" },
    { key: "status", label: "Status" },
    { key: "updated", label: "Updated" },
];

function statusText(t) {
    if (t.draft) return "Draft";
    if (t.shared) return "Shared";
    return "Private";
}

function statusPill(t) {
    if (t.draft) return <span className="tms-chip status-draft">Draft</span>;
    if (t.shared) return <span className="tms-chip status-shared">Shared</span>;
    return <span className="tms-chip status-private">Private</span>;
}

export function TemplateList({ templates, brandByKey, meId, canSelect, selected, onToggleSelected }) {
    const [sortKey, setSortKey] = useState("updated");
    const [dir, setDir] = useState("desc");

    const clickSort = (k) => {
        if (sortKey === k) setDir(dir === "asc" ? "desc" : "asc");
        else {
            setSortKey(k);
            setDir(k === "name" || k === "category" ? "asc" : "desc");
        }
    };

    const sorted = [...templates].sort((a, b) => {
        const brandA = brandByKey(a.brand_key)?.label || a.brand_key;
        const brandB = brandByKey(b.brand_key)?.label || b.brand_key;
        const ownerA = a.owner?.name || "";
        const ownerB = b.owner?.name || "";
        const pull = (t, k) => {
            switch (k) {
                case "name": return (t.name || "").toLowerCase();
                case "category": return (t.category || "").toLowerCase();
                case "brand": return ((k === "brand" ? (t === a ? brandA : brandB) : "") || "").toLowerCase();
                case "owner": return (t === a ? ownerA : ownerB).toLowerCase();
                case "status": return statusText(t);
                case "updated": return t.updated_at || "";
                default: return "";
            }
        };
        const va = pull(a, sortKey);
        const vb = pull(b, sortKey);
        if (va < vb) return dir === "asc" ? -1 : 1;
        if (va > vb) return dir === "asc" ? 1 : -1;
        return 0;
    });

    const Arrow = () => (dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />);

    return (
        <div className="tms-card overflow-hidden" data-testid="template-list-table">
            <table className="w-full">
                <thead>
                    <tr>
                        {canSelect && <th style={{ width: 40 }}></th>}
                        {COLS.map((c) => (
                            <th
                                key={c.key}
                                className="sort-header"
                                onClick={() => clickSort(c.key)}
                                data-testid={`sort-header-${c.key}`}
                            >
                                <span className="inline-flex items-center gap-1">
                                    {c.label}
                                    {sortKey === c.key && <Arrow />}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((t) => {
                        const b = brandByKey(t.brand_key);
                        const rowSelectable = canSelect ? canSelect(t) : false;
                        const isSelected = selected && selected.has(t.id);
                        return (
                            <tr key={t.id} className="tr-row border-b" style={{ borderColor: "var(--tms-border)", background: isSelected ? "rgba(142,199,64,0.10)" : undefined }} data-testid={`template-row-${t.id}`}>
                                {canSelect && (
                                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        {rowSelectable ? (
                                            <input
                                                type="checkbox"
                                                checked={!!isSelected}
                                                onChange={() => onToggleSelected && onToggleSelected(t.id)}
                                                data-testid={`template-select-row-${t.id}`}
                                                aria-label={`Select ${t.name}`}
                                            />
                                        ) : null}
                                    </td>
                                )}
                                <td className="px-4 py-3">
                                    <div className="flex items-start gap-2" style={{ minWidth: 0 }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Link
                                                to={`/templates/${t.id}`}
                                                style={{ color: "var(--tms-navy)", fontWeight: 600, textDecoration: "none" }}
                                            >
                                                {t.name}
                                            </Link>
                                            {(t.description || "").trim() && (
                                                <div
                                                    data-testid={`template-row-desc-${t.id}`}
                                                    style={{
                                                        fontSize: 12,
                                                        color: "var(--tms-faint)",
                                                        fontStyle: "italic",
                                                        marginTop: 2,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        maxWidth: 360,
                                                    }}
                                                    title={t.description}
                                                >
                                                    {t.description}
                                                </div>
                                            )}
                                        </div>
                                        <TemplateInfoPopover template={t} testIdSuffix={`row-${t.id}`} />
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className="cat-chip">{t.category}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="inline-flex items-center gap-2">
                                        <BrandMark brand={b} code={b?.short_code || t.brand_key.toUpperCase()} size="sm" />
                                        <span style={{ fontSize: 13 }}>{b?.label || t.brand_key}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="inline-flex items-center gap-2">
                                        <Avatar initials={t.owner?.initials} name={t.owner?.name} size="sm" />
                                        <span style={{ fontSize: 13 }}>{t.owner_id === meId ? "You" : t.owner?.name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3">{statusPill(t)}</td>
                                <td className="px-4 py-3" style={{ fontSize: 12, color: "var(--tms-muted)" }}>
                                    {t.draft ? `Draft · ${relativeTime(t.updated_at)}` : relativeTime(t.updated_at)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
